/**
 * Standalone clinician settings pages — Issue 16 follow-up.
 *
 * Before: clicking "Schedule / Insurance / Practice Profile / Public Profile"
 * in the sidebar scroll-jumped you on the dashboard. From any other page
 * (which is the audit's actual complaint) those anchor links did nothing.
 *
 * After: each of these has its own real route inside DashboardShell so the
 * sidebar entry navigates predictably from anywhere. The actual UI is
 * shared with the dashboard sections via the same components — there is
 * exactly one source of truth per concept.
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { clinicianNavItems } from '../components/brand/navItems';
import { ClinicianInsuranceCard } from '../components/brand/InsuranceCards';
import { PracticeProfileCard } from '../components/brand/PracticeProfileCard';
import { ClinicianShareCard } from '../components/brand/ShareProfile';
import { Button } from '../components/ui/button';
import { Calendar, Video, MapPin, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----------------------------------------------------------------------------
// Shared shell wrapper — keeps the four pages visually identical & DRY.
// ----------------------------------------------------------------------------
function SettingsShell({ title, subtitle, testId, children }) {
  const location = useLocation();
  return (
    <DashboardShell
      navItems={clinicianNavItems({ pathname: location.pathname })}
      title={title}
      subtitle={subtitle}
      testId={testId}
    >
      <div className="max-w-4xl">{children}</div>
    </DashboardShell>
  );
}

// ----------------------------------------------------------------------------
// /clinician/schedule — full visit list, no longer crammed into a 4-col tile
// ----------------------------------------------------------------------------
export function ClinicianSchedulePage() {
  const { getAuthHeader } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/appointments`, getAuthHeader())
      .then((r) => setAppointments(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [getAuthHeader]);

  return (
    <SettingsShell
      title="Schedule"
      subtitle="Your full upcoming visits list."
      testId="settings-schedule"
    >
      <div className="cn-card p-6">
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-7 h-7 animate-spin text-cn-primary mx-auto" /></div>
        ) : appointments.length === 0 ? (
          <div className="text-center py-12 rounded-2xl bg-cn-bg/60 border border-dashed border-cn-border" data-testid="schedule-empty">
            <Calendar className="w-10 h-10 text-cn-text-muted mx-auto mb-3" />
            <p className="text-sm text-cn-text-muted">No appointments yet. Your scheduled visits will show up here.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="schedule-list">
            {appointments.map((appt, i) => (
              <li key={appt.id} className="flex items-center gap-3 p-3 rounded-2xl border border-cn-border/70 hover:bg-cn-bg/60 transition-colors" data-testid={`schedule-appointment-${i}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${appt.appointment_type === 'telehealth' ? 'bg-cn-primary/10 text-cn-primary' : 'bg-cn-secondary/15 text-cn-secondary'}`}>
                  {appt.appointment_type === 'telehealth' ? <Video className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-cn-text dark:text-white text-sm truncate">{appt.patient?.first_name} {appt.patient?.last_name}</p>
                  <p className="text-[11px] text-cn-text-muted">{appt.scheduled_date} · {appt.scheduled_time} · {appt.duration_minutes}m</p>
                </div>
                {appt.appointment_type === 'telehealth' && appt.status !== 'completed' && (
                  <Link to={`/telehealth/${appt.id}`}>
                    <Button className="cn-btn cn-btn-primary !py-1.5 !px-3 !text-xs">Start</Button>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </SettingsShell>
  );
}

// ----------------------------------------------------------------------------
// /clinician/insurance — same card the dashboard uses, with breathing room
// ----------------------------------------------------------------------------
export function ClinicianInsurancePage() {
  const { getAuthHeader } = useAuth();
  return (
    <SettingsShell
      title="Insurance"
      subtitle="Your professional liability + accepted payer panels. CaraLoom never accepts the patient's insurance — only displays it."
      testId="settings-insurance"
    >
      <ClinicianInsuranceCard getAuthHeader={getAuthHeader} />
    </SettingsShell>
  );
}

// ----------------------------------------------------------------------------
// /clinician/practice-profile — the editable practice profile
// ----------------------------------------------------------------------------
export function ClinicianPracticeProfilePage() {
  const { getAuthHeader } = useAuth();
  return (
    <SettingsShell
      title="Practice Profile"
      subtitle="The clinical practice details displayed to patients searching CaraLoom."
      testId="settings-practice-profile"
    >
      <PracticeProfileCard getAuthHeader={getAuthHeader} onSaved={() => {}} />
    </SettingsShell>
  );
}

// ----------------------------------------------------------------------------
// /clinician/public-profile — shareable public profile + share-link generator
// ----------------------------------------------------------------------------
export function ClinicianPublicProfilePage() {
  const { user, getAuthHeader } = useAuth();
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    axios.get(`${API}/clinician/profile`, getAuthHeader())
      .then((r) => setProfile(r.data))
      .catch(() => {});
  }, [getAuthHeader]);
  return (
    <SettingsShell
      title="Public Profile"
      subtitle="The page patients see when they search for you, plus a shareable link you can drop into your bio or email signature."
      testId="settings-public-profile"
    >
      <ClinicianShareCard profile={profile} user={user} />
    </SettingsShell>
  );
}
